import { useEffect } from "react";
import { Reveal, SectionHead } from "@/components/Reveal";
import { GhostLink, AssessmentButton } from "@/components/Ctas";
import { CALENDLY_URL } from "@/config";

const CHALLENGES = [
  "Processes that depend too much on one person",
  "Too many priorities competing for attention",
  "Teams working hard without a clear operating rhythm",
  "Manual workflows creating unnecessary friction",
  "Difficulty turning strategy into consistent execution",
  "Systems that worked when the business was smaller but no longer scale",
  "Knowing AI could help, but not knowing where it actually makes sense",
];

const AREAS = [
  {
    number: "01",
    title: "Clarify the Direction",
    description:
      "Help the business understand where to focus and what matters most.",
    services: ["Business strategy", "Product/service strategy", "Strategic planning", "Prioritization", "Roadmapping", "Opportunity mapping"],
    testid: "businesses-area-direction",
  },
  {
    number: "02",
    title: "Strengthen the System",
    description:
      "Build better processes and ways of working.",
    services: ["Process improvement", "Operating systems", "Program/project management", "Workflow design", "Team ways of working", "Leadership & delegation", "Continuous improvement"],
    testid: "businesses-area-system",
  },
  {
    number: "03",
    title: "Add Intelligence",
    description:
      "Use AI and automation where they can create meaningful value.",
    services: ["AI opportunity mapping", "AI readiness", "Workflow automation", "AI-powered operations", "Practical AI implementation"],
    testid: "businesses-area-intelligence",
  },
];

const ENGAGEMENTS = [
  {
    name: "Strategy & Planning",
    description:
      "For businesses that need clarity around priorities, opportunities, products, services or growth direction.",
    testid: "engagement-strategy-planning",
  },
  {
    name: "Systems & Process Improvement",
    description:
      "For businesses that need better workflows, processes and operating systems.",
    testid: "engagement-systems-process",
  },
  {
    name: "Program & Product Management",
    description:
      "For businesses that need experienced support to move important initiatives forward.",
    testid: "engagement-program-product",
  },
  {
    name: "AI & Automation",
    description:
      "For businesses looking to identify and implement practical AI and automation opportunities.",
    testid: "engagement-ai-automation",
  },
];

const METHOD = ["ASSESS", "CLARIFY", "PRIORITIZE", "BUILD", "LEARN", "ADAPT"];

export default function Businesses() {
  useEffect(() => {
    document.title = "For Service Businesses — KP Strategy";
    return () => {
      document.title =
        "KP Strategy — Design your business like a product. Run it like a system. Scale it with AI.";
    };
  }, []);

  return (
    <main data-testid="businesses-page">
      {/* Hero */}
      <section className="pt-[72px]" data-testid="businesses-hero">
        <div className="mx-auto max-w-7xl px-4 pb-16 pt-16 sm:px-6 sm:pt-24 lg:px-8 lg:pb-20 lg:pt-28">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-medium uppercase tracking-[0.3em] text-amber">
              <span className="inline-block h-px w-10 bg-amber" aria-hidden="true" />
              For Service Businesses
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-7 max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl md:text-6xl">
              Build a more scalable way of working.
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-body sm:text-lg">
              Your business may be growing, but the way you work shouldn&apos;t
              have to become more complicated with it. KP Strategy helps service
              businesses strengthen strategy, improve processes, build better
              systems and identify practical opportunities for AI and
              automation.
            </p>
          </Reveal>
          <Reveal delay={0.24}>
            <div className="mt-9 flex flex-wrap items-center gap-6">
              <AssessmentButton testid="businesses-cta-assessment" dark={false} />
              <GhostLink testid="businesses-cta-book-call" href={CALENDLY_URL}>
                Book an Intro Call
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Challenges */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="businesses-challenges">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="01 — The Reality"
            title="Growth can expose the gaps in your system."
            testid="businesses-challenges-head"
          />
          <ul className="mt-12 grid max-w-4xl grid-cols-1 gap-x-12 sm:grid-cols-2" data-testid="businesses-challenges-list">
            {CHALLENGES.map((c, i) => (
              <Reveal key={c} delay={i * 0.05}>
                <li className="flex items-baseline gap-3 border-b border-line py-4 text-base leading-relaxed text-body">
                  <span className="text-amber" aria-hidden="true">✦</span>
                  {c}
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* How KP Strategy helps */}
      <section className="border-t border-line bg-parch py-20 sm:py-28" data-testid="businesses-areas">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="02 — How I Help"
            title="Product thinking. Systems thinking. Practical execution."
            testid="businesses-areas-head"
          />
          <div className="mt-14 border-t border-ink/10">
            {AREAS.map((a, i) => (
              <Reveal key={a.number} delay={i * 0.08}>
                <div
                  data-testid={a.testid}
                  className="grid grid-cols-1 gap-6 border-b border-ink/10 py-10 lg:grid-cols-12 lg:gap-10"
                >
                  <div className="lg:col-span-4">
                    <p className="font-serif text-5xl font-light text-amber/90">
                      {a.number}
                    </p>
                    <h3 className="mt-4 font-serif text-2xl font-normal text-ink sm:text-3xl">
                      {a.title}
                    </h3>
                    <p className="mt-3 max-w-sm text-base leading-relaxed text-body">
                      {a.description}
                    </p>
                  </div>
                  <ul className="flex flex-wrap content-start gap-2 lg:col-span-8 lg:pt-2">
                    {a.services.map((s) => (
                      <li
                        key={s}
                        className="border border-line bg-paper px-3.5 py-2 font-mono text-[11px] uppercase tracking-wider text-body transition-colors duration-300 hover:border-amber/60 hover:text-ink"
                      >
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* The Approach */}
      <section className="border-t border-line py-20 sm:py-24" data-testid="businesses-approach">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="03 — The Approach"
            title="Strengthen the business before adding more complexity."
            copy="Create the structure and systems your business needs to grow sustainably."
            testid="businesses-approach-head"
          />
          <Reveal delay={0.15}>
            <ol className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-0" data-testid="businesses-approach-strip">
              {METHOD.map((step, i) => (
                <li key={step} className="flex items-center">
                  <span className="flex items-baseline gap-2">
                    <span className="font-mono text-[11px] text-amber">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="font-mono text-sm font-medium uppercase tracking-[0.15em] text-ink">
                      {step}
                    </span>
                  </span>
                  {i < METHOD.length - 1 && (
                    <span className="hidden px-4 text-ink/30 sm:inline" aria-hidden="true">
                      →
                    </span>
                  )}
                </li>
              ))}
            </ol>
          </Reveal>
        </div>
      </section>

      {/* Engagement formats */}
      <section className="border-t border-line py-20 sm:py-28" data-testid="businesses-engagements">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            chapter="04 — Working Together"
            title="What working together can look like"
            copy="Flexible formats, shaped around the problem — not rigid packages."
            testid="businesses-engagements-head"
          />
          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:gap-8">
            {ENGAGEMENTS.map((e, i) => (
              <Reveal key={e.name} delay={i * 0.08} className="h-full">
                <div
                  data-testid={e.testid}
                  className="group h-full border border-line bg-white p-8 transition-all duration-300 hover:-translate-y-1 hover:border-amber/60 sm:p-10"
                >
                  <h3 className="font-serif text-2xl font-normal text-ink">
                    {e.name}
                  </h3>
                  <span className="mt-5 block h-px w-10 bg-amber transition-all duration-500 group-hover:w-full" />
                  <p className="mt-5 text-base leading-relaxed text-body">
                    {e.description}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-ink py-24 text-paper sm:py-32" data-testid="businesses-final-cta">
        <div className="mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-amber">
              05 — Next Step
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-3xl font-light leading-[1.12] tracking-tight sm:text-4xl md:text-5xl">
              Your business is growing. Your way of working should grow with
              it.
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mx-auto mt-7 max-w-xl text-base leading-relaxed text-paper/70 sm:text-lg">
              Let&apos;s identify what&apos;s creating friction — and what would
              make the biggest difference next.
            </p>
          </Reveal>
          <Reveal delay={0.26}>
            <div className="mt-10 flex flex-wrap items-center justify-center gap-7">
              <AssessmentButton testid="businesses-final-cta-assessment" dark />
              <GhostLink
                testid="businesses-final-cta-book-call"
                href={CALENDLY_URL}
                dark
              >
                Book an Intro Call
              </GhostLink>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
